import React, { useCallback } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { useDispatch } from 'react-redux'
import { Link, RouteComponentProps, useLocation, withRouter } from 'react-router-dom'
import { Card, CardBody, Col, Container, FormGroup, Row } from 'reactstrap'
import logoLight from 'src/assets/images/logo-owgi.svg'
import Loader from 'src/components/atoms/Loader'
import { FieldInput } from 'src/components/molecules/FieldInput'
import Toast from 'src/components/Toast/Toast'
import { ROUTES } from 'src/constants/routes'
import useShallowEqualSelector from 'src/hooks/useShallowEqualSelector'
import { AuthActions } from 'src/redux/actions/Auth'
import { State } from 'src/redux/reducers'
import { ColoredButton } from 'src/stories/atoms/ColoredButton'
import { ButtonWithIcon } from 'src/stories/molecules/ButtonWithIcon'
import TooltipBase from 'src/components/atoms/TooltipBase'
import Footer from 'src/components/Layout/Footer'

const SignIn: React.VFC<RouteComponentProps> = () => {
  const dispatch = useDispatch()
  const methods = useForm({ mode: 'onBlur' })
  const queryString = new URLSearchParams(useLocation().search)
  const redirectTo = queryString.get('redirectTo')
  const brandId = queryString.get('brandId')
  const [isRevealPassword, setIsRevealPassword] = React.useState(false)

  const { loading: authLoading, error: authError, notified: authNotified } = useShallowEqualSelector(
    (state: State) => state.Auth
  ).toJS()

  const togglePassword = () => {
    setIsRevealPassword(prevState => !prevState)
  }
  const handleSubmit = useCallback(
    values => {
      dispatch(AuthActions.getSigned({ user: { ...values }, redirectTo, brandId }))
    },
    [brandId, dispatch, redirectTo]
  )

  // TODO 初回でloadingがtrueになってしまう原因を突き止める
  React.useEffect(() => {
    authLoading && dispatch(AuthActions.setLoading(false))
  })
  return (
    <>
      {authLoading && <Loader />}
      {authError && !authNotified && <Toast color={'error'} message={authError} stateName={'Auth'} />}
      <div className={'account-pages mt-5 mb-5'}>
        <Container>
          <Row className={'justify-content-center'}>
            <Col md={8} lg={6} xl={4}>
              <Card>
                <CardBody className={'p-4 position-relative'}>
                  <div className={'text-center w-75 m-auto pb-3 display-4'}>
                    <Link to="/" className={'display-4'}>
                      <span className={'logo-lg'}>
                        <img src={logoLight} alt={'Owgi'} height={'60'} />
                      </span>
                    </Link>
                  </div>
                  <FormProvider {...methods}>
                    <form onSubmit={methods.handleSubmit(handleSubmit)}>
                      <FieldInput
                        id="email"
                        name="email"
                        type="email"
                        label="メールアドレス"
                        placeholder="Enter your email"
                        requiredText="メールアドレスを入力してください。"
                        autoComplete="email"
                      />
                      <FieldInput
                        id="password"
                        name="password"
                        type={isRevealPassword ? 'text' : 'password'}
                        label="パスワード"
                        placeholder="Enter your password"
                        requiredText="パスワードを入力してください。"
                        autoComplete="current-password"
                        addonSettings={{
                          addonType: 'append',
                          addon: (
                            <>
                              <TooltipBase
                                id="reveal-password"
                                message={isRevealPassword ? 'パスワードを非表示にする' : 'パスワードを表示する'}
                              />
                              <ButtonWithIcon
                                id="reveal-password"
                                iconName={isRevealPassword ? 'eye' : 'eye-off'}
                                onClick={togglePassword}
                                role="presentation"
                                className="password-reveal"
                              />
                            </>
                          )
                        }}
                      />
                      <FormGroup className={'d-flex justify-content-center'}>
                        <ColoredButton type={'submit'}>ログインする</ColoredButton>
                      </FormGroup>
                    </form>
                  </FormProvider>
                </CardBody>
              </Card>
            </Col>
          </Row>

          <Row className={'mt-1'}>
            <Col className={'col-12 text-center'}>
              <p className={'text-muted'}>
                パスワードを忘れた場合は
                <Link to={ROUTES.forgetPassword()} className={'text-primary ml-1'}>
                  こちら
                </Link>
              </p>
            </Col>
          </Row>
          <Row>
            <Footer className={'footer-no-space'} />
          </Row>
        </Container>
      </div>
    </>
  )
}

export default withRouter(SignIn)
